import Type from "./Types";
import * as Cesium from "cesium";

const spaceCameraOverrideState = {
  active: false, // Space is held
  prev: null, // Previous camera flags
};

const cameraLockState = {
  count: 0,
  saved: null, // { enableRotate, enableTranslate, enableZoom, enableTilt, enableLook }
};


/**
 * Safe keyboard panning. No-throw.
 */
export function initKeyboardPanControls({ viewer, appModeRef, savedHotkeysRef, turningArcModeRef }) {
  const computeZoomLevel = () => {
    try {
      const scene = viewer?.scene;
      const cam = viewer?.camera;
      const canvas = scene?.canvas || viewer?.canvas;
      if (!scene || !cam || !canvas) return 0;

      const w = Math.max(canvas.clientWidth || canvas.width || 1, 1);
      const h = Math.max(canvas.clientHeight || canvas.height || 1, 1);
      const y = h / 2;
      const ellipsoid = scene?.globe?.ellipsoid || Cesium?.Ellipsoid?.WGS84 || null;
      let mpp;

      if (ellipsoid && cam?.pickEllipsoid && Cesium?.Cartesian2 && Cesium?.Cartesian3) {
        const left = cam.pickEllipsoid(new Cesium.Cartesian2(0, y), ellipsoid);
        const right = cam.pickEllipsoid(new Cesium.Cartesian2(w, y), ellipsoid);
        if (left && right) {
          const dist = Cesium.Cartesian3.distance(left, right);
          mpp = dist / Math.max(w, 1);
        }
      }

      if (!mpp || !Number.isFinite(mpp)) {
        const height = cam?.positionCartographic?.height ?? 1;
        const fovy = cam?.frustum?.fovy ?? (Cesium?.Math?.toRadians ? Cesium.Math.toRadians(60) : Math.PI / 3);
        mpp = (2 * height * Math.tan(fovy / 2)) / Math.max(h, 1);
      }

      const lat = cam?.positionCartographic?.latitude ?? 0;
      const R = (Cesium?.WebMercatorProjection && new Cesium.WebMercatorProjection().ellipsoid.maximumRadius) || 6378137;
      const denom = Math.max(mpp * 256, 1e-6);
      const value = (Math.cos(lat) * 2 * Math.PI * R) / denom;
      const z = Math.log2(Math.max(value, 1e-6));
      return Number.isFinite(z) ? z : 0;
    } catch {
      return 0;
    }
  };

  const getMoveRate = () => {
    const z = computeZoomLevel();
    if (z >= 26) return 0.1;
    if (z >= 25) return 0.5;
    if (z >= 23) return 3;
    if (z >= 20) return 9;
    if (z >= 19) return 40;
    if (z >= 15) return 90;
    return 200;
  };

  const isModeAllowed = () => {
    try {
      const mode = appModeRef?.current;
      const M = Type?.MODE || {};
      const allowed = new Set([M.VIEW, M.EDIT, M.EDITE]);
      return allowed.has(mode) || !M || !mode;
    } catch {
      return true;
    }
  };

  const normalizeKey = (k) => {
    if (!k) return "";
    let key = k.toLowerCase();
    if (key === "control") key = "ctrl";
    if (key === " ") key = "space";
    return key;
  };

  const handleKeyDown = (event) => {
    if (turningArcModeRef.current)
      return
    try {
      const t = event?.target;
      if (typeof Element !== "undefined" && t instanceof Element) {
        const tag = (t.tagName || "").toUpperCase();
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        let el = t;
        while (el && el !== document.body) {
          if (el?.isContentEditable) return;
          el = el?.parentElement;
        }
      }

      const pressed = [];
      if (event.ctrlKey) pressed.push("ctrl");
      if (event.shiftKey) pressed.push("shift");
      if (event.altKey) pressed.push("alt");
      if (event.metaKey) pressed.push("meta");
      pressed.push(normalizeKey(event.key));

      const combo = pressed.join("+");

      // match with saved hotkeys
      const hotkeys = savedHotkeysRef?.current || [];
      const match = hotkeys.find((hk) => hk && hk.combination.join("+").toLowerCase() === combo);

      if (!match) return;

      const camera = viewer?.camera;
      if (!camera || !isModeAllowed()) return;

      const moveRate = getMoveRate();

      switch (match.id) {
        case "pan-up":
          camera.moveUp?.(moveRate);
          event.preventDefault();
          break;
        case "pan-down":
          camera.moveDown?.(moveRate);
          event.preventDefault();
          break;
        case "pan-left":
          camera.moveLeft?.(moveRate);
          event.preventDefault();
          break;
        case "pan-right":
          camera.moveRight?.(moveRate);
          event.preventDefault();
          break;
        default:
          break;
      }
    } catch {
      // swallow errors
    }
  };

  try {
    document?.addEventListener?.("keydown", handleKeyDown, { capture: false });
  } catch {}

  return () => {
    try {
      document?.removeEventListener?.("keydown", handleKeyDown, { capture: false });
    } catch {}
  };
}

/**
 * Safe zoom computation. No-throw.
 */
export function computeZoomLevel(viewer) {
  try {
    const scene = viewer?.scene;
    const camera = viewer?.camera;
    const canvas = viewer?.canvas || scene?.canvas;
    if (!scene || !camera || !canvas) return 0;

    const w = Math.max(canvas.clientWidth || canvas.width || 1, 1);
    const h = Math.max(canvas.clientHeight || canvas.height || 1, 1);
    const y = h / 2;

    const ellipsoid = scene?.globe?.ellipsoid || Cesium?.Ellipsoid?.WGS84 || null;

    let mpp;

    if (ellipsoid && camera?.pickEllipsoid && Cesium?.Cartesian2 && Cesium?.Cartesian3) {
      const left = camera.pickEllipsoid(new Cesium.Cartesian2(0, y), ellipsoid);
      const right = camera.pickEllipsoid(new Cesium.Cartesian2(w, y), ellipsoid);
      if (left && right) {
        const dist = Cesium.Cartesian3.distance(left, right);
        mpp = dist / Math.max(w, 1);
      }
    }

    if (!mpp || !Number.isFinite(mpp)) {
      const height = camera?.positionCartographic?.height ?? 1;
      const fovy = camera?.frustum?.fovy ?? (Cesium?.Math?.toRadians ? Cesium.Math.toRadians(60) : Math.PI / 3);
      mpp = (2 * height * Math.tan(fovy / 2)) / Math.max(h, 1);
    }

    const lat = camera?.positionCartographic?.latitude ?? 0;
    const R = (Cesium?.WebMercatorProjection && new Cesium.WebMercatorProjection().ellipsoid.maximumRadius) || 6378137;
    const denom = Math.max(mpp * 256, 1e-6);
    const value = (Math.cos(lat) * 2 * Math.PI * R) / denom;
    const z = Math.log2(Math.max(value, 1e-6));
    return Number.isFinite(z) ? z : 0;
  } catch {
    return 0;
  }
}

// Function to disable camera controls in the Cesium viewer
export const disableCameraControls = (viewer, originalCameraControls) => {
  if (!viewer || spaceCameraOverrideState.active) return false;

  const controller = viewer.scene?.screenSpaceCameraController;
  if (!controller) return false;

  // If already locked by another tool, just increment and return
  if (cameraLockState.count > 0) {
    cameraLockState.count += 1;
    return true;
  }

  // First locker: capture a single global snapshot
  cameraLockState.saved = {
    enableRotate: controller.enableRotate,
    enableTranslate: controller.enableTranslate,
    enableZoom: controller.enableZoom,
    enableTilt: controller.enableTilt,
    enableLook: controller.enableLook,
  };

  // Keep backward-compat: fill the ref too (but do not rely on it)
  if (originalCameraControls && typeof originalCameraControls === "object") {
    originalCameraControls.current = { ...cameraLockState.saved };
  }

  // Apply locked state
  controller.enableRotate = false;
  controller.enableTranslate = false;
  controller.enableZoom = true;
  controller.enableTilt = false;
  controller.enableLook = false;

  cameraLockState.count = 1;
  return true;
};


// Function to restore camera controls in the Cesium viewer
export const restoreCameraControls = (viewer, originalCameraControls) => {
  if (!viewer) return;

  const controller = viewer.scene?.screenSpaceCameraController;
  if (!controller) return;

  // If we are not in ref-count mode, fallback to old behavior
  // (keeps compatibility for any weird call order)
  if (cameraLockState.count <= 0) {
    if (!spaceCameraOverrideState.active && originalCameraControls?.current) {
      controller.enableRotate = originalCameraControls.current.enableRotate;
      controller.enableTranslate = originalCameraControls.current.enableTranslate;
      controller.enableZoom = originalCameraControls.current.enableZoom;
      controller.enableTilt = originalCameraControls.current.enableTilt;
      controller.enableLook = originalCameraControls.current.enableLook;
    }
    return;
  }

  cameraLockState.count -= 1;

  // Still locked by other tools
  if (cameraLockState.count > 0) return;

  // Last unlock: restore using the global snapshot
  const saved = cameraLockState.saved;
  cameraLockState.saved = null;
  cameraLockState.count = 0;

  // If Space is currently overriding, do NOT restore now (it would fight Space).
  // Defer restore until after Space keyup handler restores its own prev state.
  if (spaceCameraOverrideState.active) {
    // Put snapshot back so we can restore later if needed
    cameraLockState.saved = saved;
    cameraLockState.count = 0;
    return;
  }

  if (!saved) return;

  controller.enableRotate = saved.enableRotate;
  controller.enableTranslate = saved.enableTranslate;
  controller.enableZoom = saved.enableZoom;
  controller.enableTilt = saved.enableTilt;
  controller.enableLook = saved.enableLook;
};


// Install global Space behavior
export const installGlobalSpaceCameraUnlock = (viewer) => {
  if (!viewer || !viewer.scene || !viewer.scene.screenSpaceCameraController) return;

  const controller = viewer.scene.screenSpaceCameraController;

  const onKeyDown = (e) => {
    // Only Space and ignore repeat while active
    if (e.code !== "Space" || spaceCameraOverrideState.active) return;

    // Prevent page scroll on Space
    e.preventDefault();

    spaceCameraOverrideState.active = true;

    // Save current camera flags
    spaceCameraOverrideState.prev = {
      enableRotate: controller.enableRotate,
      enableTranslate: controller.enableTranslate,
      enableZoom: controller.enableZoom,
      enableTilt: controller.enableTilt,
      enableLook: controller.enableLook,
    };

    // Temporarily unlock camera
    controller.enableRotate = true;
    controller.enableTranslate = true;
    controller.enableZoom = true;
    controller.enableTilt = true;
    controller.enableLook = true;
  };

  const onKeyUp = (e) => {
    if (e.code !== "Space") return;
    if (!spaceCameraOverrideState.active) return;

    spaceCameraOverrideState.active = false;

    const prev = spaceCameraOverrideState.prev;
    if (!prev) {
      spaceCameraOverrideState.prev = null;
      return;
    }

    // Restore previous camera flags
    controller.enableRotate = prev.enableRotate;
    controller.enableTranslate = prev.enableTranslate;
    controller.enableZoom = prev.enableZoom;
    controller.enableTilt = prev.enableTilt;
    controller.enableLook = prev.enableLook;

    spaceCameraOverrideState.prev = null;
      // If a restore was deferred while Space was active, apply it now
  if (cameraLockState.count === 0 && cameraLockState.saved) {
    const saved2 = cameraLockState.saved;
    cameraLockState.saved = null;

    controller.enableRotate = saved2.enableRotate;
    controller.enableTranslate = saved2.enableTranslate;
    controller.enableZoom = saved2.enableZoom;
    controller.enableTilt = saved2.enableTilt;
    controller.enableLook = saved2.enableLook;
  }

  };

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);

  // Optional cleanup if you need it
  return () => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);

    // If still active, restore once
    if (spaceCameraOverrideState.active && spaceCameraOverrideState.prev) {
      const prev = spaceCameraOverrideState.prev;
      controller.enableRotate = prev.enableRotate;
      controller.enableTranslate = prev.enableTranslate;
      controller.enableZoom = prev.enableZoom;
      controller.enableTilt = prev.enableTilt;
      controller.enableLook = prev.enableLook;
    }

    spaceCameraOverrideState.active = false;
    spaceCameraOverrideState.prev = null;
  };
};

// Double-press Space: toggle last active exclusive mode on/off
// Usage example:
//   import { useAppStore } from "../store";
//   const cleanup = installGlobalSpaceDoublePressModeToggle(useAppStore);
//   // later: cleanup?.();

export const installGlobalSpaceDoublePressModeToggle = (store, opts = {}) => {
  const doublePressMs = typeof opts.doublePressMs === "number" ? opts.doublePressMs : 300;

  if (!store || typeof store.getState !== "function") return;

  // Keep last disabled mode to restore
  const lastDisabledRef = { key: null };

  // Detect double tap on Space (tap = keyup)
  let lastTapAt = 0;

  const MODE_KEYS = [
    "moveToolMode",
    "laneMarkerStyleMode",
    "generateArc",
    "association",
    "fastAssociation",
    "createLaneletMode",
    "measureDistanceMode",
    "measureAreaMode",
    "lineIntersectMeasureMode",
    "overlayTextMode",
    "trafficSignMode",
    "trafficLightMode",
    "generateRouteMode",
    "roadMarkingMode",
    "roadMarkingMovingMode",
    "autoSketcherMode",
    "drivewayMode",
  ];

  const SETTERS = {
    moveToolMode: "setMoveToolMode",
    laneMarkerStyleMode: "setLaneMarkerStyleMode",
    generateArc: "setGenerateArc",
    association: "setAssociation",
    fastAssociation: "setFastAssociation",
    createLaneletMode: "setCreateLaneletMode",
    measureDistanceMode: "setMeasureDistanceMode",
    measureAreaMode: "setMeasureAreaMode",
    lineIntersectMeasureMode: "setLineIntersectMeasureMode",
    overlayTextMode: "setOverlayTextMode",
    trafficSignMode: "setTrafficSignMode",
    trafficLightMode: "setTrafficLightMode",
    generateRouteMode: "setGenerateRouteMode",
    roadMarkingMode: "setRoadMarkingMode",
    roadMarkingMovingMode: "setRoadMarkingMovingMode",
    autoSketcherMode: "setAutoSketcherMode",
    drivewayMode: "setDrivewayMode",
  };

  const isTypingTarget = (t) => {
    const el = t;
    if (!el) return false;
    const tag = (el.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    return Boolean(el.isContentEditable);
  };

  const getActiveModeKey = (state) => {
    for (const k of MODE_KEYS) {
      if (state?.[k]) return k;
    }
    return null;
  };

  const setMode = (state, key, value) => {
    const setterName = SETTERS[key];
    const fn = setterName ? state?.[setterName] : null;
    if (typeof fn === "function") fn(Boolean(value));
  };

  const toggleModes = () => {
    const state = store.getState();
    const activeKey = getActiveModeKey(state);

    // If a mode is active -> disable it and remember it
    if (activeKey) {
      lastDisabledRef.key = activeKey;
      setMode(state, activeKey, false);
      return;
    }

    // If none active -> restore last disabled (if any)
    const lastKey = lastDisabledRef.key;
    if (lastKey) {
      setMode(state, lastKey, true);
    }
  };

  const onKeyDown = (e) => {
    if (e.code !== "Space") return;
    if (e.repeat) return;
    if (isTypingTarget(e.target)) return;

    // Prevent page scroll on Space
    e.preventDefault();
  };

  const onKeyUp = (e) => {
    if (e.code !== "Space") return;
    if (isTypingTarget(e.target)) return;

    e.preventDefault();

    const t = performance.now();
    const dt = t - lastTapAt;

    if (dt > 0 && dt <= doublePressMs) {
      lastTapAt = 0;
      toggleModes();
      return;
    }

    lastTapAt = t;
  };

  window.addEventListener("keydown", onKeyDown, { passive: false });
  window.addEventListener("keyup", onKeyUp, { passive: false });

  return () => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
  };
};

// Function to set the Cesium viewer's scene mode (3D, 2D, or Columbus View)
export const setSceneMode = (viewer, mode) => {
  if (!viewer) return; // Exit if viewer is not defined

  switch (mode) {
    case Type.SCENE.SCENE_3D:
      // Set the scene mode to 3D
      viewer.scene.mode = Cesium.SceneMode.SCENE3D;
      break;
    case Type.SCENE.SCENE_2D:
      // Set the scene mode to 2D (top-down view)
      viewer.scene.mode = Cesium.SceneMode.SCENE2D;
      break;
    case Type.SCENE.SCENE_COLUMBUS:
      // Set the scene mode to Columbus View (2.5D)
      viewer.scene.mode = Cesium.SceneMode.COLUMBUS_VIEW;
      break;
    default:
      console.warn("Invalid scene mode:", mode);
  }
};
